// deps
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { useState } from "react";
import { useIntl } from "react-intl";

// libraries
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";

// hooks
import useApiFetcher from "@raiden/library/hooks/useApiFetcher";
import useForm from "@splitfire-agency/raiden-library/dist/hooks/useForm";

// normalizers
import { usersPasswordResetsSerializeCreate } from "@raiden/library/normalizers/users/passwordResets";

// helpers
import { apiGetErrors } from "@raiden/library/helpers/api";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";

export default function AuthPasswordResetStep1(props) {
  const intl = useIntl();

  const apiFetcher = useApiFetcher();

  const [
    { data: passwordResetsCreateResponseData },
    setPasswordResetsCreateResponse,
  ] = useState(/** @type {{data: any}} */ ({ data: null }));

  const form = useForm({
    initialFields: {
      data: {
        user_type: "customer",
      },
    },
    onSubmit: handleSubmit,
  });

  /**
   * Gère la soumision du formulaire.
   */
  async function handleSubmit(fields) {
    await apiFetcher(
      generateApiUri({
        id: "@usersPasswordResets.create",
      }),
      {
        method: "POST",
        body: usersPasswordResetsSerializeCreate({ fields }),
      },
    )
      .then(function (data) {
        setPasswordResetsCreateResponse({ data });
      })
      .catch(function (error) {
        form.setErrors(
          apiGetErrors({
            error,
            detailFallback: intl.formatMessage({
              defaultMessage:
                "Une erreur est survenue lors de la réinitialisation.",
            }),
          }),
        );

        setPasswordResetsCreateResponse({
          data: null,
        });
      });
  }

  return (
    <VStack spacing="1.5rem" padding="1rem">
      {passwordResetsCreateResponseData ? (
        <>
          <Text
            color="gray.700"
            fontWeight="700"
            fontSize="1.5rem"
            textAlign="center">
            {intl.formatMessage({
              defaultMessage: "Consultez votre boite e-mail",
            })}
          </Text>

          <VStack spacing="1rem" align="flex-start">
            <Text>
              {intl.formatMessage(
                {
                  defaultMessage:
                    "Un e-mail vous a été envoyé à l'adresse suivante: {email}. Merci de cliquer sur le lien qui s'y trouve afin de réinitialiser votre mot de passe.",
                },
                {
                  email: passwordResetsCreateResponseData?.data?.email,
                },
              )}
            </Text>

            <NextLink
              href={generateFrontPath({ id: "@account" })}
              passHref={true}
              legacyBehavior>
              <Button w="100%" variant="solid" as="a">
                {intl.formatMessage({
                  defaultMessage: "Retourner à la page de connexion",
                })}
              </Button>
            </NextLink>
          </VStack>
        </>
      ) : (
        <>
          {form.getError("_detail") && (
            <Alert status="error">
              <AlertIcon />

              {form.getError("_detail")}
            </Alert>
          )}

          <Text
            color="gray.700"
            fontWeight="700"
            fontSize="1.5rem"
            textAlign="center">
            {intl.formatMessage({
              defaultMessage: "Réinitialisation de votre mot de passe",
            })}
          </Text>

          <Box width="100%" as="form" action="#" onSubmit={form.onSubmit}>
            <VStack spacing="1rem" align="flex-start">
              <Text>
                {intl.formatMessage({
                  defaultMessage:
                    "Saisissez votre identifiant ou votre adresse e-mail de connexion. Un e-mail contenant un lien de réinitialisation de votre mot de passe vous sera envoyé sous peu.",
                })}
              </Text>

              <FormControl id="email" isRequired={true}>
                <FormLabel>
                  {intl.formatMessage({
                    defaultMessage: "Votre identifiant ou votre adresse e-mail",
                  })}
                </FormLabel>

                <Input
                  {...form.inputProps({ name: "data[email]" })}
                  placeholder={intl.formatMessage({
                    defaultMessage: "dupond@exemple.fr",
                  })}
                  isRequired={true}
                />
              </FormControl>

              <Button type="submit" w="100%" variant="solid">
                {intl.formatMessage({
                  defaultMessage: "Recevoir un e-mail de réinitialisation",
                })}
              </Button>

              <Box w="100%" textAlign="center" mb="1rem">
                <NextLink
                  href={generateFrontPath({
                    id: "@account",
                    query: { tab: "sign-in" },
                  })}
                  passHref={true}
                  legacyBehavior>
                  <Link color="blue.500">
                    {intl.formatMessage({
                      defaultMessage: "Retour à la connexion",
                    })}
                  </Link>
                </NextLink>
              </Box>
            </VStack>
          </Box>
        </>
      )}
    </VStack>
  );
}

AuthPasswordResetStep1.propTypes = {};
